import React from "react";

import { ACTIONS_LIST } from "../../shared/utils/constants";
import {
  accessControl,
  getAccessMode,
  AccessMode
} from "../../core/access-control";
interface Props {
  permissions: Record<string, any>;
  requiredPage: string; // Page permission required to see the component
  children: JSX.Element; // The component to be rendered
  checkPermission?: ({
    // Custom function which can be provided to check the permission
    requiredPage,
    requiredFunctions,
    sourceEntityId,
    permissions
  }: {
    requiredPage: string;
    sourceEntityId?: string | Array<string>;
    requiredFunctions: Array<(typeof ACTIONS_LIST)[keyof typeof ACTIONS_LIST]>;
    permissions: Record<string, any>;
  }) => boolean;
  requiredFunctions?: Array<(typeof ACTIONS_LIST)[keyof typeof ACTIONS_LIST]>; // The functional apis the user should have access to render this component
  showPermissionDenied?: boolean; // Whether to show permission denied or a blank page
  sourceEntityId?: string | Array<string>; // If present, the source entity id for which the access to functional api should be present
  some?: boolean; // If true, the component will be rendered if the user has access to any of the functional apis
  isUserType?: boolean;
  disableNotHide?: boolean;
  isLenderPlatform?: boolean;
  hideIfRequiredFunctionsPresent?: boolean;
}

function PermissionDenied() {
  return (
    <div className="flex flex-row items-center justify-center w-full h-full">
      <p className="flex text-3xl font-semibold py-8">Permission Denied</p>
    </div>
  );
}

export function checkPermissionDefault({
  requiredPage,
  sourceEntityId,
  requiredFunctions = [],
  permissions,
  some,
  hideIfRequiredFunctionsPresent
}: {
  requiredPage: string;
  sourceEntityId?: string | Array<string>;
  requiredFunctions?: string[];
  permissions: Record<string, any>;
  some?: boolean;
  hideIfRequiredFunctionsPresent?: boolean; // If true, the component will not be rendered if requiredFunctions are present but not allowed
}) {
  if (isRbacEnabled()) {
    return true;
  }
  let sourceEntityIds: Array<string> = [];
  let isAllowed = !!permissions?.[requiredPage];
  const pagePermissions = permissions?.[requiredPage] ?? {};

  if (!sourceEntityId || !isAllowed) {
    // User is simply checking for page access or if he does not have the access to the page,
    // return now itself
    return isAllowed;
  }
  if (typeof sourceEntityId === "string") {
    sourceEntityIds = [sourceEntityId];
  }
  if (Array.isArray(sourceEntityId)) {
    sourceEntityIds = [...sourceEntityId];
  }
  // User has page access
  if (sourceEntityIds?.length || requiredFunctions?.length > 0) {
    isAllowed = sourceEntityIds?.some((s) => {
      return !!pagePermissions?.[s];
    });

    // If hideIfRequiredFunctionsPresent is true, we will not render the component if requiredFunctions are present
    if (hideIfRequiredFunctionsPresent) {
      const isForbidden = requiredFunctions.some((func) =>
        sourceEntityIds?.some((s) => pagePermissions?.[s]?.includes(func))
      );
      return isAllowed && !isForbidden;
    }

    if (isAllowed && requiredFunctions?.length) {
      isAllowed =
        isAllowed && some
          ? requiredFunctions.some((func) => {
              return sourceEntityIds?.some((s) => {
                return pagePermissions?.[s]?.includes(func);
              });
            })
          : requiredFunctions.every((func) => {
              return sourceEntityIds?.some((s) => {
                return pagePermissions?.[s]?.includes(func);
              });
            });
    }
    // }
  }
  return isAllowed;
}

export function checkPermissionDefaultLender(
  requiredPage: string,
  requiredFunctions: string[],
  pagePermission: Record<string, any>,
  some?: boolean
) {
  let isAllowed = (Object.keys(pagePermission) || []).includes(requiredPage);

  isAllowed =
    isAllowed && !some
      ? requiredFunctions?.every((func) =>
          pagePermission?.[requiredPage]?.includes(func)
        )
      : requiredFunctions?.some((func) =>
          pagePermission?.[requiredPage]?.includes(func)
        );
  return isAllowed;
}

export default function PrivateComponent({
  permissions,
  children,
  requiredPage,
  checkPermission,
  requiredFunctions = [],
  showPermissionDenied = false, // Used only for pages and tabs - used to determine if page or component
  sourceEntityId,
  some = false,
  disableNotHide = false,
  isLenderPlatform = false,
  hideIfRequiredFunctionsPresent = false
}: Props) {
  const isAllowed = isLenderPlatform
    ? getAccessMode() === AccessMode.UiConfig
      ? accessControl.can(
          { page: requiredPage, actions: requiredFunctions, some },
          null
        )
      : checkPermissionDefaultLender?.(
          requiredPage,
          requiredFunctions,
          permissions,
          some
        )
    : accessControl.can(
        {
          page: requiredPage,
          entityIds: sourceEntityId,
          actions: requiredFunctions,
          some,
          forbidWhenActionsPresent: hideIfRequiredFunctionsPresent
        },
        permissions,
        checkPermission
          ? () =>
              checkPermission({
                requiredPage,
                requiredFunctions,
                permissions,
                sourceEntityId
              })
          : undefined
      );

  if (isAllowed || disableNotHide) {
    let props = {};

    if (showPermissionDenied) {
      // indicates that this is a page
      props = { pageName: requiredPage };
    } else if (!isAllowed && disableNotHide) {
      props = {
        disabled: disableNotHide,
        style: { cursor: "not-allowed", opacity: "0.5" }
      };
    }

    return (
      <>
        {React.Children.map(children, (child) =>
          React.cloneElement(child, props)
        )}
      </>
    );
  }

  if (showPermissionDenied) {
    return <PermissionDenied />;
  }

  return null;
}
